import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Countdown from "@/components/Countdown";
import { Reveal } from "@/components/Reveal";
import { money } from "@/lib/api";

export default function HolidayDrop({ product }) {
  const isLive = product.status === "live";
  const soldOut = product.status === "sold_out";
  const imgFront = product.images?.[0];
  const imgBack = product.images?.[1];

  return (
    <section className="relative overflow-hidden border-b border-wire noise-overlay" data-testid="holiday-drop-section">
      <div className="absolute inset-0 grunge-texture opacity-[0.12]" />
      <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-12">
        <div className="relative border-b border-wire lg:col-span-6 lg:border-b-0 lg:border-r">
          <Reveal className="p-6 sm:p-12">
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden border border-wire bg-charcoal">
                <img src={imgFront} alt={`${product.name} — front view`} className="aspect-[4/5] w-full object-cover" data-testid="holiday-drop-image-front" />
              </div>
              {imgBack && (
                <div className="mt-12 overflow-hidden border border-wire bg-charcoal">
                  <img src={imgBack} alt={`${product.name} — back view`} className="aspect-[4/5] w-full object-cover" data-testid="holiday-drop-image-back" />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between px-1">
              <p className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">HOLIDAY 2026 — FRONT / BACK</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">DR-002</p>
            </div>
          </Reveal>
        </div>

        <div className="flex flex-col justify-center px-6 py-16 sm:px-12 lg:col-span-6 lg:py-28">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.4em] text-neutral-500" data-testid="holiday-drop-eyebrow">
              THE DECEMBER DROP — HOLIDAY 2026
            </p>
            <h2 className="mt-5 font-display text-2xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[0.95] tracking-tight text-white" data-testid="holiday-drop-name">
              Limited Edition<br />
              <span className="text-outline" style={{ WebkitTextStroke: "1.5px #f5f5f5", fontSize: "clamp(1.5rem, 3.2vw, 3.6rem)" }}>Pink Backstabber</span>
            </h2>
            <p className="mt-5 font-script text-4xl text-neutral-100" data-testid="holiday-drop-price">{money(product.price)}</p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-400" data-testid="holiday-drop-run">
                {product.run_size ? `${product.run_size} UNITS — NEVER RESTOCKED` : "LIMITED RUN — NEVER RESTOCKED"}
              </p>
              {product.members_first && (
                <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-400">MEMBERS FIRST — 24H</p>
              )}
            </div>

            {soldOut ? (
              <p className="mt-10 font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-500" data-testid="holiday-drop-status">
                Sold Out — Archived
              </p>
            ) : isLive ? (
              <div className="mt-10">
                <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-white" data-testid="holiday-drop-status">Live Now</p>
                {product.stock && (
                  <p className="mt-3 font-mono text-[11px] tracking-[0.3em] text-neutral-300" data-testid="holiday-drop-stock">
                    {product.stock_total} / {product.run_size || product.stock_total} LEFT
                  </p>
                )}
              </div>
            ) : (
              product.drop_date && <Countdown target={product.drop_date} large testId="holiday-drop-countdown" />
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              {soldOut ? (
                <Link to="/shop" data-testid="holiday-drop-cta"
                  className="border border-wire px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                  SHOP WHAT'S LIVE
                </Link>
              ) : (
                <Link to={`/product/${product.slug}`} data-testid="holiday-drop-cta"
                  className="group flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                  {isLive ? "SHOP NOW" : "GET NOTIFIED"}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </div>
            <p className="mt-8 font-mono text-[10px] tracking-[0.3em] text-neutral-600">WHEN IT'S GONE, IT'S ARCHIVE.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
